// v3 truck, authored the way age-of-mcp writes it: a HORSE-DRAWN CART — a sturdy
// work horse in the shafts pulling a four-wheeled wooden wagon with low plank
// sides, a buckboard seat and iron-shod wheels. The v3 source reuses the v2
// horse model wholesale (yawed, scaled and slid into the shafts) and the shared
// styleV3 box/cyl makers. The library has no game-truck module, so the cargo
// FILL constant is inlined and the v2 horse is reproduced via the library's
// `critter.quadLegs` helper (the one helper the horse imports). Rendered at the
// empty-bed default fill (v = 0), matching the flattened source. Ported 1:1 from
// age-of-mcp's v3/truck.ts.
import * as THREE from "three";
import { defineModel, critter, styleV3, type Part } from "../../lib";

const { box, cyl } = styleV3;

// Inlined from age-of-mcp's truck.ts: the cargo fill resolution.
const TRUCK_FILL_STEPS = 4;

// --- The v2 horse, reproduced 1:1 (it imports only critter.quadLegs) ---------
const HORSE_COATS = [
  { coat: "#9a6234", shade: "#86532a", mane: "#3a2a18", muzzle: "#6e4a28", leg: "#7a4e26", hoof: "#2e2620" },
  { coat: "#6e4526", shade: "#5e3a20", mane: "#1f1813", muzzle: "#4a3018", leg: "#241c14", hoof: "#16120e" },
  { coat: "#cfcac0", shade: "#b8b2a6", mane: "#8e8880", muzzle: "#8a847a", leg: "#a8a196", hoof: "#48423a" },
];
const NECK_LEAN = 0.62;

function horseParts(v: number): Part[] {
  const c = HORSE_COATS[v % HORSE_COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.078, dz: 0.03, bodyY: 0.158, rTop: 0.0085, rFoot: 0.006,
    color: c.leg, hoof: { color: c.hoof, r: 0.0085, h: 0.015 },
  });
  parts.push(
    { geometry: new THREE.IcosahedronGeometry(0.058, 0), color: c.coat,
      position: [-0.002, 0.158, 0], scale: [1.7, 0.95, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.shade,
      position: [0.07, 0.152, 0], scale: [1.0, 1.05, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.coat,
      position: [-0.075, 0.165, 0], scale: [1.05, 0.95, 0.85] },
    { geometry: new THREE.CylinderGeometry(0.018, 0.03, 0.105, 5), color: c.coat,
      position: [0.105, 0.205, 0], rotation: [0, 0, -NECK_LEAN] },
    { geometry: new THREE.BoxGeometry(0.014, 0.115, 0.013), color: c.mane,
      position: [0.0855, 0.219, 0.005], rotation: [0, 0, -NECK_LEAN] },
    { geometry: new THREE.BoxGeometry(0.05, 0.03, 0.026), color: c.coat,
      position: [0.156, 0.252, 0], rotation: [0, 0, -0.35] },
    { geometry: new THREE.ConeGeometry(0.0115, 0.032, 5), color: c.muzzle,
      position: [0.185, 0.243, 0], rotation: [0, 0, -Math.PI / 2 - 0.35] },
    { geometry: new THREE.IcosahedronGeometry(0.008, 0), color: c.mane,
      position: [0.162, 0.272, 0] },
    { geometry: new THREE.ConeGeometry(0.0055, 0.018, 4), color: c.shade,
      position: [0.147, 0.272, 0.011], rotation: [0.15, 0, 0.1] },
    { geometry: new THREE.ConeGeometry(0.0055, 0.018, 4), color: c.shade,
      position: [0.147, 0.272, -0.011], rotation: [-0.15, 0, 0.1] },
    { geometry: new THREE.IcosahedronGeometry(0.0036, 0), color: "#1c140e",
      position: [0.155, 0.26, 0.0145] },
    { geometry: new THREE.IcosahedronGeometry(0.0036, 0), color: "#1c140e",
      position: [0.155, 0.26, -0.0145] },
    { geometry: new THREE.CylinderGeometry(0.0085, 0.0065, 0.05, 4), color: c.mane,
      position: [-0.127, 0.1535, 0], rotation: [0, 0, 2.29] },
    { geometry: new THREE.IcosahedronGeometry(0.016, 0), color: c.mane,
      position: [-0.148, 0.112, 0], scale: [1.2, 1.9, 0.7] },
  );
  return parts;
}

// --- Palette -----------------------------------------------------------------
const FLOOR  = "#9a6a38";
const SIDE   = "#73522e";
const FRAME  = "#5b4226";
const SPOKE  = "#a9783f";
const TYRE   = "#2b2722";
const HUB    = "#55493d";
const SHAFT  = "#6b4423";
const SEAT   = "#3f2c18";
const LOAD   = "#cba65f";
const BARREL = "#7a5230";
const HOOP   = "#2f2722";

const HORSE_COAT  = 1;
const HORSE_SCALE = 1.62;
const HORSE_Z     = 0.40;
const HORSE_YAW   = -Math.PI / 2;

// Reuse the horse wholesale: yaw it to face +z, scale it, slide it to the front.
function harnessHorse(): Part[] {
  const qYaw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), HORSE_YAW);
  const offset = new THREE.Vector3(0, 0, HORSE_Z);
  return horseParts(HORSE_COAT).map((p): Part => {
    const pos = new THREE.Vector3(...(p.position ?? [0, 0, 0]))
      .multiplyScalar(HORSE_SCALE).applyQuaternion(qYaw).add(offset);
    const baseEuler = new THREE.Euler(
      p.rotation?.[0] ?? 0, p.rotation?.[1] ?? 0, p.rotation?.[2] ?? 0,
      (p.eulerOrder as THREE.EulerOrder) ?? "XYZ");
    const qFinal = qYaw.clone().multiply(new THREE.Quaternion().setFromEuler(baseEuler));
    const e = new THREE.Euler().setFromQuaternion(qFinal, "XYZ");
    const s = p.scale ?? 1;
    const scale: number | [number, number, number] = typeof s === "number"
      ? s * HORSE_SCALE
      : [s[0] * HORSE_SCALE, s[1] * HORSE_SCALE, s[2] * HORSE_SCALE];
    return {
      geometry: p.geometry, color: p.color,
      position: [pos.x, pos.y, pos.z],
      rotation: [e.x, e.y, e.z], eulerOrder: "XYZ", scale,
    };
  });
}

// A spoked, iron-shod wooden wheel centred at (x, z), axle along x.
function wheel(x: number, z: number, R: number): Part[] {
  const out: Part[] = [];
  const tube = R * 0.16;
  out.push({
    geometry: new THREE.TorusGeometry(R - tube, tube, 4, 14),
    color: TYRE, position: [x, R, z], rotation: [0, Math.PI / 2, 0],
  });
  out.push(cyl(R * 0.22, R * 0.22, 0.06, 6, HUB, [x, R, z], [0, 0, Math.PI / 2]));
  for (let i = 0; i < 3; i++) {
    out.push(box(0.013, 2 * (R - tube) * 0.94, 0.013, SPOKE, [x, R, z], [(i * Math.PI) / 3, 0, 0]));
  }
  return out;
}

const WHX   = 0.16;
const R_F   = 0.062;
const R_R   = 0.084;
const Z_F   = 0.03;
const Z_R   = -0.24;
const BEDY  = 0.175;
const BEDZ  = -0.07;
const BEDL  = 0.40;
const SIDEH = 0.09;

function partsFor(v: number): Part[] {
  const parts: Part[] = [];

  parts.push(...wheel(-WHX, Z_F, R_F), ...wheel(WHX, Z_F, R_F));
  parts.push(...wheel(-WHX, Z_R, R_R), ...wheel(WHX, Z_R, R_R));
  parts.push(box(0.40, 0.022, 0.022, TYRE, [0, R_F, Z_F]));
  parts.push(box(0.40, 0.024, 0.024, TYRE, [0, R_R, Z_R]));

  const SX = 0.135;
  const top = BEDY + SIDEH / 2 + 0.012;
  for (const sx of [-1, 1]) {
    parts.push(box(0.024, 0.03, BEDL, FRAME, [sx * SX, BEDY - 0.02, BEDZ]));
  }
  parts.push(box(0.255, 0.028, BEDL, FLOOR, [0, BEDY, BEDZ]));
  for (const sx of [-1, 1]) {
    parts.push(box(0.02, SIDEH, BEDL - 0.02, SIDE, [sx * SX, top, BEDZ]));
    parts.push(box(0.028, 0.02, BEDL, FRAME, [sx * SX, top + SIDEH / 2, BEDZ]));
  }
  parts.push(box(0.29, SIDEH, 0.02, SIDE, [0, top, BEDZ + BEDL / 2 - 0.01]));
  parts.push(box(0.29, SIDEH - 0.01, 0.02, SIDE, [0, top - 0.005, BEDZ - BEDL / 2 + 0.01]));

  const seatY = top + SIDEH / 2;
  parts.push(box(0.25, 0.022, 0.06, SEAT, [0, seatY + 0.025, BEDZ + BEDL / 2 - 0.04]));
  parts.push(box(0.25, 0.045, 0.016, SEAT, [0, seatY + 0.055, BEDZ + BEDL / 2 - 0.008]));

  for (const sx of [-1, 1]) {
    parts.push(box(0.018, 0.018, 0.34, SHAFT, [sx * 0.10, 0.205, 0.255], [0.05, 0, 0]));
  }
  parts.push(box(0.22, 0.018, 0.018, SHAFT, [0, 0.21, 0.40]));

  const fill = Math.max(0, Math.min(1, v / TRUCK_FILL_STEPS));
  if (fill > 0) {
    const h = 0.04 + fill * 0.09;
    parts.push(box(0.225, h, BEDL - 0.06, LOAD, [0, BEDY + 0.014 + h / 2, BEDZ]));
    if (fill > 0.5) {
      for (const z of [BEDZ - 0.08, BEDZ + 0.05]) {
        parts.push(cyl(0.045, 0.045, 0.20, 8, BARREL, [0, BEDY + 0.04 + h, z], [0, 0, Math.PI / 2]));
        for (const bx of [-0.055, 0.055]) {
          parts.push(cyl(0.046, 0.046, 0.018, 8, HOOP, [bx, BEDY + 0.04 + h, z], [0, 0, Math.PI / 2]));
        }
      }
    }
  }

  parts.push(...harnessHorse());

  return parts;
}

export const truckV3 = defineModel({
  id: "truck-v3",
  batchId: "45-optimized",
  name: "Truck (v3)",
  tags: ["vehicle","truck","low-poly","age-of-mcp","v3"],
  summary: "Low-poly truck.",
  description: "Ported from the age-of-mcp gallery, v3 style.",
  author: "age-of-mcp",
  footprint: 0.5,
  parts: () => partsFor(0),
  animations: [],
});
